import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box, Button, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import {
  listMachines, uploadMachineSbom, getMachineFindings, rescanMachine, deleteMachine, putMachineSettings,
} from "../api/machineSbomApi";
import { getEdges } from "../api/edgeApi";
import { getTenants } from "../api/tenantApi";
import MachineComponents from "../components/MachineComponents";
import {
  InfoNote, Muted, Pager, Pill, WarnNote, cardSx, cellSx, dangerBtnSx, fieldSx, headCellSx, outlineBtnSx, primaryBtnSx,
} from "../components/sbomUi";
import { hasCap, sbomErrorMessage } from "../utils/capabilities";

const PAGE = 10;
const STATUS_TONE = { queued: "progress", running: "progress", completed: "success", failed: "critical" };
const SEVERITY_ORDER = ["critical", "high", "medium", "low"];

function Findings({ accessToken, machine, onClose }) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });

  useEffect(() => {
    let cancelled = false;
    getMachineFindings(accessToken, machine.id, { limit: PAGE, offset })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => toast.error(sbomErrorMessage(err, t("sbom.errors.loadFindings"))));
    return () => { cancelled = true; };
  }, [accessToken, machine.id, machine.last_scanned_at, offset, t]);

  return (
    <Box component="section" aria-label={t("sbom.findings.aria")} sx={cardSx}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>{machine.machine_name} · {machine.serial_number}</Typography>
          <Muted>{t("sbom.findings.hint")}</Muted>
        </Box>
        <Button size="small" variant="outlined" sx={outlineBtnSx} onClick={onClose}>{t("common.close")}</Button>
      </Box>
      {data.items.length === 0 && (
        <Box sx={{ border: "1px dashed var(--border-strong)", borderRadius: "8px", p: 3, textAlign: "center", mt: 2 }}>
          <Typography sx={{ color: "var(--text-primary)", fontWeight: 600 }}>{t("sbom.findings.emptyTitle")}</Typography>
          <Muted>{t("sbom.findings.emptyHint")}</Muted>
        </Box>
      )}
      {data.items.length > 0 && (
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>{["cve", "severity", "component", "summary"].map((h) => <TableCell key={h} sx={headCellSx}>{t(`sbom.findings.cols.${h}`)}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((f) => (
                <TableRow key={`${f.cve_id}-${f.component_name}`} hover>
                  <TableCell sx={{ ...cellSx, fontWeight: 600, whiteSpace: "nowrap" }}>{f.cve_id}</TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                    {f.is_kev && <><Pill tone="kev" text={t("sbom.severity.kev")} />{" "}</>}
                    <Pill tone={SEVERITY_ORDER.includes(f.severity) ? f.severity : "medium"} text={t(`sbom.severity.${SEVERITY_ORDER.includes(f.severity) ? f.severity : "medium"}`)} />
                  </TableCell>
                  <TableCell sx={cellSx}>{f.component_name} {f.component_version || ""}</TableCell>
                  <TableCell sx={{ ...cellSx, color: "var(--text-secondary)" }}>{f.summary ? f.summary.slice(0, 280) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Pager offset={offset} shown={data.items.length} total={data.total} pageSize={PAGE} onChange={setOffset} />
    </Box>
  );
}

export default function MachineSbom() {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const me = useSelector((s) => s.session?.me);
  const isSuper = me?.global_role === "superadmin";
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [data, setData] = useState({ items: [], usage: null });
  const [edges, setEdges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [componentsOf, setComponentsOf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ machineName: "", serialNumber: "", edgeId: "", file: null });
  const [limits, setLimits] = useState({ machineLimit: "", cooldown: "" });

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    try {
      const d = await listMachines(accessToken, isSuper ? tenantId || undefined : undefined);
      setData(d);
      if (d.usage) setLimits({ machineLimit: String(d.usage.limit), cooldown: String(d.usage.replace_cooldown_hours) });
    } catch (err) {
      toast.error(sbomErrorMessage(err, t("sbom.errors.loadList")));
    }
  }, [accessToken, isSuper, tenantId, t]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!isSuper || !accessToken) return;
    getTenants(accessToken, {}).then((d) => setTenants(Array.isArray(d) ? d : d.items || [])).catch(() => setTenants([]));
  }, [accessToken, isSuper]);
  useEffect(() => {
    if (!accessToken || (isSuper && !tenantId)) { setEdges([]); return; }
    getEdges(accessToken, isSuper ? tenantId : null).then((e) => setEdges(Array.isArray(e) ? e : [])).catch(() => setEdges([]));
  }, [accessToken, isSuper, tenantId]);
  const active = data.items.some((m) => m.status === "queued" || m.status === "running");
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [active, refresh]);

  if (!hasCap(me, "security.sbom")) {
    return <Typography sx={{ color: "var(--text-muted)" }}>{t("sbom.disabled")}</Typography>;
  }

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (ok) toast.success(typeof ok === "function" ? ok(r) : ok);
      await refresh();
    } catch (err) {
      toast.error(sbomErrorMessage(err, t("sbom.errors.failed")));
    } finally {
      setBusy(false);
    }
  };

  const canUpload = form.machineName.trim() && form.serialNumber.trim() && form.file && (!isSuper || tenantId);
  const submit = (ev) => {
    ev.preventDefault();
    run(async () => {
      const r = await uploadMachineSbom(accessToken, { file: form.file, machineName: form.machineName.trim(), serialNumber: form.serialNumber.trim(), edgeId: form.edgeId, tenantId: isSuper ? tenantId : undefined });
      setForm({ machineName: "", serialNumber: "", edgeId: "", file: null });
      return r;
    }, (r) => (r.unchanged ? t("sbom.upload.same") : t("sbom.upload.started")));
  };
  const remove = (m) => {
    const days = data.usage?.burn_days ?? 30;
    if (!window.confirm(t("sbom.machines.confirmDelete", { name: m.machine_name, days }))) return;
    run(async () => { await deleteMachine(accessToken, m.id); if (selected?.id === m.id) setSelected(null); if (componentsOf?.id === m.id) setComponentsOf(null); }, t("sbom.machines.deleted"));
  };
  const saveLimits = () => run(
    () => putMachineSettings(accessToken, tenantId, { machineLimit: Number(limits.machineLimit), replaceCooldownHours: Number(limits.cooldown) }),
    t("sbom.limits.saved"),
  );
  const u = data.usage;
  const percent = u && u.limit > 0 ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;

  return (
    <Box sx={{ color: "var(--text-primary)", display: "grid", gap: 3, maxWidth: 1200, pb: 4 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("sbom.title")}</Typography>
        <Typography sx={{ color: "var(--text-muted)", fontSize: 14, mt: 0.5 }}>
          {t("sbom.subtitle")}
        </Typography>
      </Box>

      <InfoNote>
        <Trans i18nKey="sbom.note" components={{ b: <strong /> }} />
      </InfoNote>

      {isSuper && (
        <TextField select size="small" label={t("sbom.tenant")} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} inputProps={{ "aria-label": t("sbom.tenant") }}
          sx={{ ...fieldSx, maxWidth: 320 }} value={tenantId} onChange={(e) => { setTenantId(e.target.value); setSelected(null); }}>
          <option value="">{t("sbom.allTenants")}</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </TextField>
      )}

      {u && (
        <Box component="section" aria-label={t("sbom.quota.aria")} sx={cardSx}>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ minWidth: 200 }}>
              <Muted>{t("sbom.quota.label")}</Muted>
              <Typography sx={{ fontSize: 34, fontWeight: 700, lineHeight: 1.2, color: "var(--text-primary)" }}>{u.used} / {u.limit}</Typography>
              <LinearProgress variant="determinate" value={percent} aria-label={t("sbom.quota.progress")}
                sx={{ mt: 1, height: 8, borderRadius: 4, backgroundColor: "var(--border)", "& .MuiLinearProgress-bar": { backgroundColor: percent >= 100 ? "var(--danger)" : "var(--accent-navy)" } }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Muted>
                {t("sbom.quota.policy", { burnDays: u.burn_days, hours: u.replace_cooldown_hours })}
              </Muted>
              {u.limit === 0 && (
                <Box sx={{ mt: 1 }}><WarnNote>{t("sbom.quota.none")}</WarnNote></Box>
              )}
            </Box>
          </Box>
          {isSuper && tenantId && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mt: 3, pt: 2, borderTop: "1px solid var(--border)" }}>
              <TextField size="small" type="number" label={t("sbom.limits.machineLimit")} inputProps={{ min: 0, "aria-label": t("sbom.limits.machineLimit") }} sx={fieldSx}
                value={limits.machineLimit} onChange={(e) => setLimits({ ...limits, machineLimit: e.target.value })} />
              <TextField size="small" type="number" label={t("sbom.limits.cooldown")} inputProps={{ min: 0, "aria-label": t("sbom.limits.cooldownAria") }} sx={fieldSx}
                value={limits.cooldown} onChange={(e) => setLimits({ ...limits, cooldown: e.target.value })} />
              <Button variant="contained" sx={primaryBtnSx} disabled={busy || limits.machineLimit === "" || limits.cooldown === ""} onClick={saveLimits}>{t("common.save")}</Button>
            </Box>
          )}
        </Box>
      )}

      <Box component="form" onSubmit={submit} aria-label={t("sbom.upload.aria")} sx={cardSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>{t("sbom.upload.title")}</Typography>
        <Muted>{t("sbom.upload.hint")}</Muted>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start", mt: 2 }}>
          <TextField size="small" label={t("sbom.upload.machineName")} inputProps={{ maxLength: 120 }} sx={fieldSx} value={form.machineName} onChange={(e) => setForm({ ...form, machineName: e.target.value })} />
          <TextField size="small" label={t("sbom.upload.serial")} inputProps={{ maxLength: 120 }} sx={fieldSx} value={form.serialNumber} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })} />
          <TextField select size="small" label={t("sbom.upload.edge")} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} inputProps={{ "aria-label": t("sbom.upload.edgeAria") }}
            sx={fieldSx} value={form.edgeId} onChange={(e) => setForm({ ...form, edgeId: e.target.value })}>
            <option value="">—</option>
            {edges.map((e) => <option key={e.id} value={e.id}>{e.name || e.edge_id}</option>)}
          </TextField>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 40 }}>
            <Button component="label" variant="outlined" sx={outlineBtnSx}>
              {t("sbom.upload.choose")}
              <input hidden aria-label={t("sbom.upload.fileAria")} type="file" onClick={(e) => { e.target.value = ""; }} accept=".json,.csv,application/json,text/csv" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
            </Button>
            <Muted>{form.file ? form.file.name : t("sbom.upload.noFile")}</Muted>
          </Box>
        </Box>
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Button type="submit" variant="contained" sx={primaryBtnSx} disabled={busy || !canUpload}>{t("sbom.upload.submit")}</Button>
          {isSuper && !tenantId && <Muted>{t("sbom.upload.pickTenant")}</Muted>}
        </Box>
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid var(--border)" }}>
          <Muted>
            <Trans i18nKey="sbom.upload.csvHelp" components={{ c: <code /> }} />
          </Muted>
        </Box>
      </Box>

      <Box component="section" aria-label={t("sbom.machines.aria")} sx={cardSx}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>{t("sbom.machines.title")}</Typography>
        {data.items.length === 0 && (
          <Box sx={{ border: "1px dashed var(--border-strong)", borderRadius: "8px", p: 4, textAlign: "center" }}>
            <Typography sx={{ color: "var(--text-primary)", fontWeight: 600 }}>{t("sbom.machines.emptyTitle")}</Typography>
            <Muted>{t("sbom.machines.emptyHint")}</Muted>
          </Box>
        )}
        {data.items.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>{["machine", "serial", "status", "scanned", "findings", ""].map((h) => <TableCell key={h || "actions"} sx={headCellSx}>{h ? t(`sbom.machines.cols.${h}`) : ""}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((m) => {
                  const statusTone = STATUS_TONE[m.status] || "neutral";
                  const statusText = STATUS_TONE[m.status] ? t(`sbom.status.${m.status}`) : m.status;
                  const open = SEVERITY_ORDER.filter((s) => m.open_by_severity?.[s]);
                  return (
                    <TableRow key={m.id} hover data-testid={`machine-${m.serial_number}`}>
                      <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{m.machine_name}</TableCell>
                      <TableCell sx={cellSx}>{m.serial_number}</TableCell>
                      <TableCell sx={cellSx}>
                        <Pill tone={statusTone} text={statusText} />
                        {m.error && <Box sx={{ mt: 0.5 }}><Muted>{m.error}</Muted></Box>}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>{m.scannable_count} / {m.component_count}</Typography>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {open.map((s) => <Box component="span" key={s} sx={{ mr: 0.75, display: "inline-block" }}><Pill tone={s} text={`${m.open_by_severity[s]} ${t(`sbom.severity.${s}`)}`} /></Box>)}
                        {!m.findings_count && <Muted>—</Muted>}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button size="small" variant="outlined" sx={outlineBtnSx} onClick={() => setSelected(m)}>{t("sbom.machines.findings")}</Button>
                          <Button size="small" variant="outlined" sx={outlineBtnSx} onClick={() => setComponentsOf(m)}>{t("sbom.machines.components")}</Button>
                          <Button size="small" variant="outlined" sx={outlineBtnSx} disabled={busy || m.status === "queued" || m.status === "running"} onClick={() => run(() => rescanMachine(accessToken, m.id), t("sbom.machines.rescanQueued"))}>{t("sbom.machines.rescan")}</Button>
                          <Button size="small" variant="outlined" sx={dangerBtnSx} disabled={busy} onClick={() => remove(m)}>{t("sbom.machines.delete")}</Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {componentsOf && (
        <MachineComponents accessToken={accessToken} machine={data.items.find((m) => m.id === componentsOf.id) || componentsOf} onChanged={refresh} onClose={() => setComponentsOf(null)} />
      )}
      {selected && <Findings accessToken={accessToken} machine={data.items.find((m) => m.id === selected.id) || selected} onClose={() => setSelected(null)} />}
    </Box>
  );
}
